import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';

function mockFindUser() {
  return new Promise<{ data: AppSliceState['user'] }>((resolve) =>
    setTimeout(
      () =>
        resolve({
          data: {
            name: 'John Doe',
            email: 'john@example.com',
            lastSeen: Date.now(),
          },
        }),
      1000
    )
  );
}

export interface AppSliceState {
  user?: { name: string; email: string; lastSeen: number };
  status: 'idle' | 'loading' | 'failed';
}

const initialState: AppSliceState = {
  user: undefined,
  status: 'idle',
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const fetchUser = createAsyncThunk('app/fetchUser', async () => {
  const response = await mockFindUser();
  // The value we return becomes the `fulfilled` action payload
  return response.data;
});

export const appSlice = createSlice({
  name: 'counter',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setUser: (state, action: PayloadAction<AppSliceState['user']>) => {
      // Redux Toolkit allows us to write "mutating" logic in reducers. It
      // doesn't actually mutate the state because it uses the Immer library,
      // which detects changes to a "draft state" and produces a brand new
      // immutable state based off those changes
      state.user = action.payload;
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.status = 'idle';
        state.user = action.payload;
      })
      .addCase(fetchUser.rejected, (state) => {
        state.status = 'failed';
        state.user = undefined;
      });
  },
});

export const { setUser } = appSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectAppSlice = (state: RootState) => state.app;

// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
export const logoutUserSession = () => (dispatch, getState) => {
  const { user } = selectAppSlice(getState());
  if (user && Date.now() - user.lastSeen > 3600) {
    dispatch(setUser(undefined));
  }
};

export default appSlice.reducer;
